import React, { useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { useAuth } from "../AuthContext";   // adjust path if needed

const FeedbackFab = () => {
  const { user } = useAuth();

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackName, setFeedbackName] = useState(
    user ? `${user.firstName || ""} ${user.middleName || ""} ${user.lastName || ""}`.trim() : ""
  );
  const [feedbackEmail, setFeedbackEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // === Your Google Form IDs (already set up) ===
  const GOOGLE_FORM_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSe_B_0B-hcd-1w_1Bpzc96hOgSlRi8QeKVPkgwBFB4kGAmJlA/formResponse";
  const NAME_ENTRY = "entry.1573159203";
  const EMAIL_ENTRY = "entry.1037494275";
  const MESSAGE_ENTRY = "entry.1558943785";
  const PAGE_ENTRY = "entry.1894176722";

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim()) return;

    setSubmitting(true);
    const formData = new FormData();
    formData.append(NAME_ENTRY, feedbackName || "Anonymous");
    formData.append(EMAIL_ENTRY, feedbackEmail || "no-email@school.com");
    formData.append(MESSAGE_ENTRY, feedbackText);
    formData.append(PAGE_ENTRY, window.location.pathname); // auto detects current page

    try {
      await fetch(GOOGLE_FORM_URL, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });

      setSubmitted(true);
      setFeedbackText("");
      setTimeout(() => {
        setShowFeedback(false);
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      // Google Forms with no-cors always shows error in console but still works
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <div
        className="feedback-fab"
        onClick={() => setShowFeedback(true)}
        style={{
          position: "fixed",
          bottom: "2rem",
          right: "2rem",
          background: "#3b82f6",
          color: "white",
          width: "56px",
          height: "56px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
          cursor: "pointer",
          zIndex: 999,
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.12)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        <MessageCircle size={28} />
        <div
          style={{
            position: "absolute",
            top: "-8px",
            right: "-8px",
            background: "#ef4444",
            color: "white",
            fontSize: "12px",
            fontWeight: "bold",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ↑
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedback && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowFeedback(false)}
        >
          <div
            className="dashboard-card"
            style={{
              width: "90%",
              maxWidth: "520px",
              padding: "2rem",
              position: "relative",
              borderRadius: "12px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowFeedback(false)}
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <X size={26} />
            </button>

            <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Send Feedback
            </h3>
            <p style={{ color: "#666", fontSize: "14px", marginBottom: "1.5rem" }}>
              Help us improve this page!
            </p>

            <form onSubmit={submitFeedback}>
              <input
                type="text"
                placeholder="Your Name"
                value={feedbackName}
                onChange={(e) => setFeedbackName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  marginBottom: "1rem",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "15px",
                }}
              />
              <input
                type="email"
                placeholder="Your Email (optional)"
                value={feedbackEmail}
                onChange={(e) => setFeedbackEmail(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  marginBottom: "1rem",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "15px",
                }}
              />
              <textarea
                required
                rows={6}
                placeholder="What do you like? Any bugs? Suggestions?"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  resize: "vertical",
                  fontSize: "15px",
                }}
              />

              <button
                type="submit"
                disabled={submitting || !feedbackText.trim()}
                style={{
                  marginTop: "1.5rem",
                  width: "100%",
                  background: submitted ? "#22c55e" : "#3b82f6",
                  color: "white",
                  padding: "0.9rem",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {submitting
                  ? "Sending..."
                  : submitted
                  ? "Thank You! ✓"
                  : <>
                      Send Feedback <Send size={20} />
                    </>}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackFab;